// src/components/GeminiTTSPage.js

import React, { useState } from 'react';
import { auth } from '../firebaseConfig';

function GeminiTTSPage({ userId }) {
  const [text, setText] = useState('');
  const [model, setModel] = useState('gemini-2.5-flash-preview-tts');
  const [speechStyle, setSpeechStyle] = useState(''); // New state for speech style
  const [audioUrl, setAudioUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [costEstimate, setCostEstimate] = useState('');

  const availableGeminiModels = [
    { id: 'gemini-2.5-flash-preview-tts', name: 'Gemini 2.5 Flash TTS (Preview)' },
    { id: 'gemini-2.5-pro-preview-tts', name: 'Gemini 2.5 Pro TTS (Preview)' },
  ];

  // Exemplary presets for voice style
  const voiceStylePresets = [
    'a warm, friendly tone',
    'an authoritative voice',
    'a soft, calming voice',
    'a slightly playful tone',
    'a clear, professional voice'
  ];

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    setAudioUrl('');
    setCostEstimate('');

    try {
      const response = await fetch('/api/gemini-tts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
        },
        body: JSON.stringify({
          text,
          model,
          speechStyle, // Pass the speech style
          userId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate audio.');
      }

      const data = await response.json();
      setAudioUrl(data.audioUrl);
      setCostEstimate(`Estimated cost for this generation: $${data.estimatedCost.toFixed(6)} (Free tier applies. Current tokens: ${data.tokenCount})`);
    } catch (err) {
      console.error("Gemini TTS generation error:", err);
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-xl shadow-2xl">
      <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Gemini Text-to-Speech</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div>
          <label htmlFor="text" className="block text-gray-700 text-sm font-semibold mb-2">
            Text to Convert:
          </label>
          <textarea
            id="text"
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 resize-y"
            rows="6"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Enter text here..."
          ></textarea>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="model" className="block text-gray-700 text-sm font-semibold mb-2">
              Gemini Model:
            </label>
            <select
              id="model"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              value={model}
              onChange={(e) => setModel(e.target.value)}
            >
              {availableGeminiModels.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="speechStyle" className="block text-gray-700 text-sm font-semibold mb-2">
              Voice Style Description (Optional):
            </label>
            <textarea
              id="speechStyle"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 transition duration-200 resize-y"
              rows="3"
              value={speechStyle}
              onChange={(e) => setSpeechStyle(e.target.value)}
              placeholder="e.g., a warm, friendly tone; an authoritative voice; a soft, calming voice"
            ></textarea>
            <div className="mt-2 flex flex-wrap gap-2">
              {voiceStylePresets.map((preset, index) => (
                <button
                  key={index}
                  onClick={() => setSpeechStyle(preset)}
                  className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition duration-200"
                >
                  {preset.split(',')[0].trim()} {/* Display just the first part for buttons */}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              This description is added to your text prompt. Gemini's ability to interpret and apply specific voice styles is based on its generative capabilities, not structured voice parameters.
            </p>
          </div>
        </div>
      </div>

      <button
        onClick={handleGenerate}
        className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={loading || !text.trim()}
      >
        {loading ? 'Generating...' : 'Generate Audio (Gemini TTS)'}
      </button>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mt-6" role="alert">
          <p className="font-bold">Error!</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {audioUrl && (
        <div className="mt-6 p-6 bg-green-50 border border-green-200 rounded-lg shadow-md">
          <h3 className="text-xl font-semibold text-green-800 mb-3">Generated Audio:</h3>
          <audio controls src={audioUrl} className="w-full">
            Your browser does not support the audio element.
          </audio>
          <p className="text-sm text-gray-600 mt-3">{costEstimate}</p>
          <p className="text-sm text-gray-600 mt-1">Audio saved to "My Files" page.</p>
        </div>
      )}
    </div>
  );
}

export default GeminiTTSPage;